import {
  Stack,
  CheckboxVisibility,
  DetailsList,
  IDetailsHeaderProps,
  IRenderFunction,
  ScrollablePane,
  SearchBox,
  Spinner,
  SpinnerSize,
  Sticky,
  StickyPositionType,
  Persona,
  PersonaSize,
  Label,
  PersonaPresence,
  MessageBar,
  MessageBarType,
  Link,
} from '@fluentui/react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { ROUTES } from '../../../config/routes';
import { HealAPI } from '../../../healApi';
import { SearchPatient } from '../../../redux/patients/types';
import { useDebounce } from '../../../util/hooks';
import { formatPhoneNumber } from '../../../util/PhoneNumber';
import { VisitQueryParams, fetchVisits } from '../../../api';
import { PatientInsuranceCards } from '../../patient/insurances/PatientInsuranceCards';
import { PatientFlags } from '../../patient/PatientFlags';
import { PatientNotes } from '../../patient/PatientNotes';
import { PatientVisits } from '../../patient/PatientVisits';
import ReferralSourcePicker from '../../patient/profile/ReferralSourcePicker';
import { useCart } from '../CartContext';
import { usePatientVisits } from '../PatientVisitsContext';
import { CreatePatient } from '../CreatePatient';
import moment from 'moment-timezone';

export function SelectPatient() {
  const { state: cart, dispatch } = useCart();
  const [searchText, setSearchText] = useState('');
  const [fetching, setFetching] = useState(false);
  const [accounts, setAccounts] = useState<SearchPatient[]>([]);
  const [patientVisits, setPatientVisits] = usePatientVisits();

  const debouncedSearchText = useDebounce(searchText, 300);

  useEffect(() => {
    let canceled = false;
    setFetching(true);
    HealAPI.searchPatient(debouncedSearchText)
      .then((r) => r.data?.filter((p) => p.status !== 'DELETED_BY_ACCOUNT'))
      .then((data) => {
        if (!canceled && data) {
          setAccounts(data);
          setFetching(false);
        }
      });

    return () => {
      canceled = true;
    };
  }, [debouncedSearchText]);

  useEffect(() => {
    if (!cart.who.patientId) {
      return;
    }

    const visitQueryParams: VisitQueryParams = {
      patient_id: cart.who.patientId,
      include: 'DOCTOR,ASSISTANT,TIME_SLOT,SERVICE_OFFERING',
      sort: 'ASC',
      from_date_time: moment().toISOString(),
    };
    fetchVisits(visitQueryParams).then(setPatientVisits);
  }, [cart.who.patientId, setPatientVisits]);

  const columns = useMemo(
    () => [
      {
        key: 'mrn',
        name: 'MRN',
        fieldName: 'mrn',
        minWidth: 80,
        maxWidth: 80,
      },
      {
        key: 'name',
        name: 'Name',
        minWidth: 80,
        maxWidth: 200,
        onRender: (item?: SearchPatient) => {
          return <Persona size={PersonaSize.size24} text={item!.fullName} imageUrl={item!.avatarUrl} />;
        },
      },
      {
        key: 'dateOfBirth',
        name: 'DOB',
        fieldName: 'dateOfBirth',
        minWidth: 120,
        maxWidth: 120,
      },
      {
        key: 'phone',
        name: 'Phone',
        fieldName: 'phone',
        minWidth: 120,
        maxWidth: 120,
        onRender: (item?: SearchPatient) => {
          return item ? formatPhoneNumber(item.phone) : '';
        },
      },
      {
        key: 'email',
        name: 'Email',
        fieldName: 'email',
        minWidth: 120,
        maxWidth: 240,
      },
    ],
    []
  );

  /**
   * Note: Dispatches by this method will be ignored at the reducer if the `accountId`
   * and `patientId` passed in the action equal what's already in the state. As reported
   * in APP-9113, refocusing the page causes FluentUI's DetailsList to re-fire its
   * `onActiveItemChanged` event. Because `set_who` is used for both _"I've selected a
   * new patient and want to fetch their data"_ as well as _"Here's the data; add it to
   * the state,"_ without this filter, refocusing the page clobbers the data we've
   * already fetched for the patient (stored in `account` and `patient`).
   * @see CartContext.tsx\@cartReducer
   */
  const onSelectPatient = useCallback(
    (patient: SearchPatient) => {
      dispatch({ type: 'set_who', data: { accountId: patient.userAccountId, patientId: patient.patientId } });
    },
    [dispatch]
  );

  const patient = cart.who.patient;

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Stack horizontal horizontalAlign="space-between">
        <Label className="ms-font-xl">Select Patient</Label>
        <CreatePatient onChange={(account) => dispatch({ type: 'set_who', data: { accountId: account.userAccountId, patientId: account.patients[0].patientId } })} />
      </Stack>
      <SearchBox className="search" placeholder="Search for an existing patient" value={searchText} onChange={(e, v) => setSearchText(v!)} data-tid="inp_search" />
      <div style={{ height: 340, position: 'relative', border: '1px solid #C8C8C8' }}>
        <ScrollablePane>
          <DetailsList
            checkboxVisibility={CheckboxVisibility.hidden}
            items={accounts}
            setKey="set"
            columns={columns}
            onActiveItemChanged={onSelectPatient}
            onRenderDetailsHeader={(detailsHeaderProps?: IDetailsHeaderProps, defaultRender?: IRenderFunction<IDetailsHeaderProps | undefined>) => (
              <Sticky stickyPosition={StickyPositionType.Header}>{!!defaultRender && defaultRender(detailsHeaderProps)}</Sticky>
            )}
          />
          {fetching && <Spinner label="Searching..." size={SpinnerSize.large} />}
          {!fetching && searchText.length > 0 && accounts.length === 0 && <div style={{ paddingLeft: 20 }}>No Results</div>}
        </ScrollablePane>
      </div>

      {patient && (
        <Stack tokens={{ childrenGap: 12 }}>
          {!patient.mainPatientOfAccount && (
            <MessageBar messageBarType={MessageBarType.error}>
              This patient is not the main patient of the account. This patient should be migrated into their own account.
            </MessageBar>
          )}
          <Persona
            size={PersonaSize.size72}
            presence={patient.completionStatus === 'PROFILE_COMPLETE' ? PersonaPresence.online : PersonaPresence.busy}
            secondaryText={`${patient.dateOfBirth} | ${patient.gender}`}
            tertiaryText={patient.email}
            imageUrl={patient.avatarUrl}
            onRenderPrimaryText={() => (
              <Link target="_blank" href={`${ROUTES.patients}/${patient.patientId}`}>
                {patient.fullName}
              </Link>
            )}
          />

          {patientVisits && <PatientVisits visits={patientVisits} />}
          <PatientFlags patientId={patient.patientId} label={'Flags:'} />

          <Stack horizontal verticalAlign="center">
            <Label style={{ marginRight: 10 }}>Referral Source</Label>
            <ReferralSourcePicker patientId={patient.patientId} />
          </Stack>

          <div style={{ marginTop: 15, paddingTop: 15, borderTop: '1px dashed #ccc' }}>
            <Label className="ms-font-xl">Patient Insurances</Label>
            <PatientInsuranceCards patientId={patient.patientId} />
          </div>

          <div style={{ marginTop: 15, paddingTop: 15, borderTop: '1px dashed #ccc' }}>
            <PatientNotes readOnly={true} patientId={patient.patientId} />
          </div>
        </Stack>
      )}
    </Stack>
  );
}
